<script setup>
import { ref, watch, onMounted, nextTick, computed, onUnmounted } from 'vue';
import Button from './Button.vue';
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(ScrollTrigger, CustomEase)

// Define service categories for the ServiceShowcase component
const serviceCategories = [
  {
    name: 'Software Oplossingen',
    services: [
      {
        title: 'Klant- & Beheerportalen',
        alt: 'Centrale omgevingen voor klanten en beheerders met gepersonaliseerde functionaliteiten.',
        image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Mobile Apps',
        alt: 'Native en hybride apps die uw diensten toegankelijk maken op elk apparaat.',
        image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Websites & CMS',
        alt: 'Moderne websites met krachtige content management systemen.',
        image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'E-commerce Oplossingen',
        alt: 'Webshops en marktplaatsen die conversie en gebruiksgemak centraal stellen.',
        image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Data Management Platform',
        alt: 'Centraliseer, verwerk en analyseer al uw bedrijfsdata.',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'API Ontwikkeling',
        alt: 'Verbind systemen en applicaties met robuuste APIs.',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'DevOps & Cloud',
        alt: 'Geautomatiseerde deployment en beheer in de cloud.',
        description: 'Onze DevOps aanpak en cloud expertise zorgen voor continue integratie, automatische deployments en proactieve monitoring. Profiteer van verbeterde stabiliteit, snellere releases en geoptimaliseerde infrastructuurkosten. We maken gebruik van moderne cloud platforms voor maximale betrouwbaarheid.',
        image: 'https://images.unsplash.com/photo-1603695762547-fba8b92eace9?w=800&auto=format&fit=crop&q=80',
      }
    ]
  },
  {
    name: 'Digitale Transformatie',
    services: [
      {
        title: 'Strategie & Advies',
        alt: 'Strategisch advies om uw digitale transformatie te versnellen.',
        image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Technische Architectuur',
        alt: 'Robuuste architectuur voor schaalbare en toekomstbestendige systemen.',
        image: 'https://images.unsplash.com/photo-1558403194-611308249627?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Data & AI Integratie',
        alt: 'Integreer AI en machine learning in uw bedrijfsprocessen.',
        image: 'https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Software Migratie',
        alt: 'Neem bestaande software over en moderniseer waar nodig.',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Replatforming',
        alt: 'Vernieuw verouderde systemen met moderne technologie.',
        image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'UX/UI Design',
        alt: 'Gebruiksvriendelijke interfaces die conversie verhogen.',
        image: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&auto=format&fit=crop&q=80',
      },
      {
        title: 'Custom Development',
        alt: 'Op maat gemaakte software oplossingen.',
        image: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=800&auto=format&fit=crop&q=80',
      }
    ]
  },
];

// Remove tab-related state since we're showing all sections
const activeServices = ref([0, 0, 0]); // Track active service for each category
const isAnimating = ref(false);

// Animation state - separate refs for each element
const section1Ref = ref(null);
const section2Ref = ref(null);
const section3Ref = ref(null);

const animatedElements = ref({
  section1: false,
  section2: false,
  section3: false
});

// Auto-play functionality for each section
const autoPlayIntervals = ref([null, null, null]);
const autoPlayDuration = 5000;
const userInteracted = ref([false, false, false]);
const autoPlayProgress = ref([0, 0, 0]); // Track progress for each section
const progressIntervals = ref([null, null, null]); // Separate intervals for progress

const serviceTitleRef = ref(null)
const serviceSubtitleRef = ref(null)
const serviceSubtitle2Ref = ref(null)
const serviceButtonRef = ref(null)
const serviceStartTextAnimation = ref(null)

onMounted(() => {
    gsap.fromTo(serviceTitleRef.value, { y: '+3vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: serviceStartTextAnimation.value, start: 'top 80%', end: 'top 20%', scrub: true, invalidateOnRefresh: true, }});
    gsap.fromTo(serviceSubtitleRef.value, { y: '+4vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: serviceStartTextAnimation.value, start: 'top 80%', end: 'top 20%', scrub: true, invalidateOnRefresh: true, }});
    gsap.fromTo(serviceSubtitle2Ref.value, { y: '+5vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: serviceStartTextAnimation.value, start: 'top 80%', end: 'top 20%', scrub: true, invalidateOnRefresh: true, }});
    
    // Target button element directly using querySelector
    const buttonElement = serviceStartTextAnimation.value.querySelector('button, a');
    if (buttonElement) {
        gsap.fromTo(buttonElement, { y: '+6vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: serviceStartTextAnimation.value, start: 'top 80%', end: 'top 20%', scrub: true, invalidateOnRefresh: true, }});
    }
})

// Computed property to get the current category's services
const currentServices = computed(() => {
  return serviceCategories[activeServices.value[0]].services || [];
});

// Computed property for total number of services in the current category
const totalServices = computed(() => {
  return currentServices.value.length;
});

// Computed property for service description text
const serviceDescription = computed(() => {
  // Use the description from the actual service data if available
  const currentService = serviceCategories[activeServices.value[0]]?.services[activeServices.value[1]];
  
  if (currentService && currentService.description) {
    return currentService.description;
  }
  
  // Default fallback description
  return "Bij c-o-d-e-s bouwen we platformen die doen wat ze moeten doen – en méér. Schaalbaar wanneer je groeit, flexibel in gebruik, krachtig in resultaat.";
});

// Computed property to find the longest description across all categories and services
const longestDescription = computed(() => {
  let longest = '';
  
  serviceCategories.forEach(category => {
    if (category.services) {
      category.services.forEach(service => {
        const description = service.description || "Bij c-o-d-e-s bouwen we platformen die doen wat ze moeten doen – en méér. Schaalbaar wanneer je groeit, flexibel in gebruik, krachtig in resultaat.";
        if (description.length > longest.length) {
          longest = description;
        }
      });
    }
  });
  
  return longest;
});

// Computed property to find the longest CTA text across all services
const longestCtaText = computed(() => {
  let longest = '';
  
  serviceCategories.forEach(category => {
    if (category.services) {
      category.services.forEach(service => {
        const ctaText = service.ctaText || 'Meer informatie';
        if (ctaText.length > longest.length) {
          longest = ctaText;
        }
      });
    }
  });
  
  return longest;
});

// Computed property for dynamic minimum height based on longest description
const descriptionMinHeight = computed(() => {
  // Create a temporary element to measure text height
  if (typeof window === 'undefined') return 'auto';
  
  const tempElement = document.createElement('div');
  tempElement.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: 100%;
    max-width: 80rem;
    font-size: 1rem;
    line-height: 1.5;
    padding: 0 1rem;
  `;
  tempElement.textContent = longestDescription.value;
  document.body.appendChild(tempElement);
  
  const height = tempElement.offsetHeight;
  document.body.removeChild(tempElement);
  
  return `${height + 24}px`; // Add some padding
});

// Computed property for dynamic minimum width based on longest CTA text
const ctaButtonMinWidth = computed(() => {
  // Create a temporary element to measure text width
  if (typeof window === 'undefined') return 'auto';
  
  const tempElement = document.createElement('span');
  tempElement.style.cssText = `
    position: absolute;
    visibility: hidden;
    white-space: nowrap;
    font-size: 1rem;
    font-weight: 500;
    padding: 0.75rem 1.5rem;
  `;
  tempElement.textContent = longestCtaText.value;
  document.body.appendChild(tempElement);
  
  const width = tempElement.offsetWidth;
  document.body.removeChild(tempElement);
  
  return `${width + 40}px`; // Add extra padding for icon and spacing
});

// Computed property to control progress animation
const shouldShowProgress = computed(() => {
  return autoPlayActive.value && autoPlayStarted.value && !userInteracted.value;
});

// Start auto-play
const startAutoPlay = (categoryIndex) => {
  if (autoPlayIntervals.value[categoryIndex]) clearInterval(autoPlayIntervals.value[categoryIndex]);
  if (progressIntervals.value[categoryIndex]) clearInterval(progressIntervals.value[categoryIndex]);
  
  // Reset progress
  autoPlayProgress.value[categoryIndex] = 0;
  
  // Start progress animation
  const progressStep = 100 / (autoPlayDuration / 50); // Update every 50ms
  progressIntervals.value[categoryIndex] = setInterval(() => {
    if (autoPlayProgress.value[categoryIndex] < 100) {
      autoPlayProgress.value[categoryIndex] += progressStep;
    }
  }, 50);
  
  autoPlayIntervals.value[categoryIndex] = setInterval(() => {
    if (!isAnimating.value) {
      navigateNext(categoryIndex, true);
    }
  }, autoPlayDuration);
};

// Stop auto-play
const stopAutoPlay = (categoryIndex) => {
  if (autoPlayIntervals.value[categoryIndex]) {
    clearInterval(autoPlayIntervals.value[categoryIndex]);
    autoPlayIntervals.value[categoryIndex] = null;
  }
  if (progressIntervals.value[categoryIndex]) {
    clearInterval(progressIntervals.value[categoryIndex]);
    progressIntervals.value[categoryIndex] = null;
  }
  autoPlayProgress.value[categoryIndex] = 0;
};

// Restart auto-play
const restartAutoPlay = (categoryIndex) => {
  autoPlayActive.value = true;
  startAutoPlay(categoryIndex);
};

// Update the watch function to handle category changes without always stopping autoplay
watch(activeServices, (newServices, oldServices) => {
  // Only stop autoplay and mark as user interaction if this wasn't triggered by navigation
  if (!isAnimating.value) {
    userInteracted.value[0] = true;
    stopAutoPlay(0);
  }
  nextTick(() => updateIndicatorPosition());
});

// Watch for all animations to complete and start autoplay
watch(animatedElements, (isComplete) => {
  if (Object.values(isComplete).every(Boolean) && !autoPlayActive.value && !userInteracted.value) {
    // Start autoplay after a small delay to ensure all animations are settled
    setTimeout(() => {
      autoPlayActive.value = true;
      serviceCategories.forEach((_, index) => {
        if (!userInteracted.value[index]) {
          startAutoPlay(index);
        }
      });
    }, 500);
  }
});

// Handle direct service selection
const selectService = (categoryIndex, serviceIndex) => {
  if (serviceIndex !== activeServices.value[categoryIndex] && !isAnimating.value) {
    stopAutoPlay(categoryIndex);
    userInteracted.value[categoryIndex] = true;
    isAnimating.value = true;
    activeServices.value[categoryIndex] = serviceIndex;
    
    setTimeout(() => {
      isAnimating.value = false;
    }, 400); // Reduced timeout to match actual content transition
  }
};

// Navigation functions
const navigatePrev = (categoryIndex) => {
  if (!isAnimating.value) {
    stopAutoPlay(categoryIndex);
    userInteracted.value[categoryIndex] = true;
    isAnimating.value = true;
    
    if (activeServices.value[categoryIndex] === 0) {
      activeServices.value[categoryIndex] = serviceCategories[categoryIndex].services.length - 1;
    } else {
      activeServices.value[categoryIndex]--;
    }
    
    setTimeout(() => {
      isAnimating.value = false;
    }, 400);
  }
};

const navigateNext = (categoryIndex, isAuto = false) => {
  if (!isAnimating.value) {
    if (!isAuto) {
      stopAutoPlay(categoryIndex);
      userInteracted.value[categoryIndex] = true;
    }
    
    isAnimating.value = true;
    
    if (activeServices.value[categoryIndex] >= serviceCategories[categoryIndex].services.length - 1) {
      activeServices.value[categoryIndex] = 0;
    } else {
      activeServices.value[categoryIndex]++;
    }
    
    setTimeout(() => {
      isAnimating.value = false;
      if (isAuto && !userInteracted.value[categoryIndex]) {
        startAutoPlay(categoryIndex);
      }
    }, 400);
  }
};

// Update the position of the active indicator
const updateIndicatorPosition = () => {
  if (!tabsContainer.value || !activeIndicator.value) return;
  
  const activeTab = tabsContainer.value.querySelector(`.tab-button[data-index="${activeCategory.value}"]`);
  if (activeTab) {
    const containerRect = tabsContainer.value.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    
    activeIndicator.value.style.left = `${tabRect.left - containerRect.left}px`;
    activeIndicator.value.style.width = `${tabRect.width}px`;
  }
};

// Handle scroll events to restart auto-play when user scrolls away
const handleScroll = () => {
  const section = document.getElementById('services-showcase');
  if (!section) return;
  
  const rect = section.getBoundingClientRect();
  const isInView = rect.top < window.innerHeight && rect.bottom > 0;
  
  // If user scrolled away and comes back, restart auto-play from current position
  if (isInView && userInteracted.value && !autoPlayActive.value) {
    userInteracted.value = false;
    restartAutoPlay();
  }
  // If user scrolled away, mark for potential restart
  else if (!isInView && autoPlayActive.value) {
    // Don't stop auto-play immediately, just mark that we're out of view
    // This allows auto-play to continue when user returns
  }
};

// Fade-in animation for section
const fadeInSection = (section) => {
  if (!section) return;
  
  section.style.opacity = '0';
  section.style.transform = 'translateY(20px)';
  
  requestAnimationFrame(() => {
    section.style.transition = 'opacity 1s ease, transform 1s ease';
    section.style.opacity = '1';
    section.style.transform = 'translateY(0)';
  });
};

onMounted(() => {
  // Start auto-play for all sections after animations complete
  setTimeout(() => {
    serviceCategories.forEach((_, index) => {
      if (!userInteracted.value[index]) {
        startAutoPlay(index);
      }
    });
  }, 3000);

  // Fade in the entire section
  const section = document.getElementById('services-showcase');
  fadeInSection(section);
});

onUnmounted(() => {
  autoPlayIntervals.value.forEach((interval, index) => {
    if (interval) stopAutoPlay(index);
  });
  progressIntervals.value.forEach((interval, index) => {
    if (interval) clearInterval(interval);
  });
});
</script>

<template>
  <section id="software-solutions" class="bg-zinc-900 dark:bg-white transition-colors duration-300 headerflip">
    <div class="wide-container relative">
      <!-- Background decorations -->
      <div class="absolute inset-0 opacity-5 dark:opacity-10 bg-[linear-gradient(to_right,#ffffff1a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#0000001a_1px,transparent_1px),linear-gradient(to_bottom,#0000001a_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] transition-colors duration-300"></div>
      
      <div class="relative z-10">
        <!-- Category sections stacked vertically -->
        <div class="space-y-32 md:space-y-40">
          <!-- Section 1: Text Left, Image Right -->
          <div v-if="serviceCategories[0]" ref="section1Ref" class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <!-- Text Content -->
            <div class="space-y-8 max-w-lg">
              <div class="space-y-4 text-left">
                <h3 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white dark:text-zinc-900">
                  Slim ontwikkeld.
                </h3>
                  <p class="text-lg md:text-xl text-zinc-300 dark:text-zinc-600 leading-relaxed">
                    Bij c-o-d-e-s bouwen we platformen die doen wat ze moeten doen – en méér. Schaalbaar wanneer je groeit, flexibel in gebruik, krachtig in resultaat.
                  </p>
              </div>
            </div>

            <!-- Image Content -->
            <div class="relative lg:my-24">
              <div class="relative h-96 rounded-lg overflow-hidden shadow-lg dark:shadow-black/30 border border-zinc-700 dark:border-zinc-200">
                <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-black via-black/95 to-black/80"></div>
                
                <transition name="smooth-fade" mode="out-in" :duration="{ enter: 400, leave: 200 }">
                  <img 
                    :key="`service-image-0-${activeServices[0]}`" 
                    :src="serviceCategories[0].services[activeServices[0]]?.image" 
                    :alt="serviceCategories[0].services[activeServices[0]]?.title"
                    class="absolute inset-0 w-full h-full object-cover object-center" 
                  />
                </transition>
                
                <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent"></div>
                
                <!-- Text overlay -->
                <div class="absolute bottom-0 left-0 right-0 p-4 text-left text-white z-10">
                  <transition name="text-slide" mode="out-in" :duration="{ enter: 400, leave: 200 }">
                    <h4 :key="`service-title-0-${activeServices[0]}`" class="text-2xl font-bold drop-shadow-md">
                      {{ serviceCategories[0].services[activeServices[0]]?.title }}
                    </h4>
                  </transition>
                </div>
              </div>
                  
                  <!-- Pagination indicators -->
                  <div class="flex gap-2 mt-4">
                    <div 
                      v-for="(service, index) in serviceCategories[0].services" 
                      :key="index" 
                      class="relative block h-1.5 w-6 rounded-xs bg-zinc-200/35 overflow-hidden cursor-pointer transition-all duration-300 hover:bg-mint-200/35" 
                      @click="selectService(0, index)"
                    >
                      <div class="absolute inset-0 bg-zinc-200/35"></div>
                      <div 
                        class="absolute inset-0 origin-left transition-all duration-300" 
                        :class="index === activeServices[0] ? 'bg-mint-500' : 'bg-zinc-400/60 scale-x-0'"
                        :style="index === activeServices[0] && !userInteracted[0] && autoPlayProgress[0] > 0 ? `transform: scaleX(${autoPlayProgress[0] / 100})` : index === activeServices[0] ? 'transform: scaleX(0)' : ''"
                      ></div>
                    </div>
                  </div>
            </div>
          </div>

          <!-- Section 2: Image Left, Text Right -->
          <div v-if="serviceCategories[1]" ref="section2Ref" class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <!-- Image Content -->
            <div class="relative lg:order-1 lg:my-24">
              <div class="relative h-96 rounded-lg overflow-hidden shadow-lg dark:shadow-black/30 border border-zinc-700 dark:border-zinc-200">
                <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-black via-black/95 to-black/80"></div>
                
                <transition name="smooth-fade" mode="out-in" :duration="{ enter: 400, leave: 200 }">
                  <img 
                    :key="`service-image-1-${activeServices[1]}`" 
                    :src="serviceCategories[1].services[activeServices[1]]?.image" 
                    :alt="serviceCategories[1].services[activeServices[1]]?.title"
                    class="absolute inset-0 w-full h-full object-cover object-center" 
                  />
                </transition>
                
                <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent"></div>
                
                <!-- Text overlay -->
                <div class="absolute bottom-0 left-0 right-0 p-4 text-left text-white z-10">
                  <transition name="text-slide" mode="out-in" :duration="{ enter: 400, leave: 200 }">
                    <h4 :key="`service-title-1-${activeServices[1]}`" class="text-2xl font-bold drop-shadow-md">
                      {{ serviceCategories[1].services[activeServices[1]]?.title }}
                    </h4>
                  </transition>
              </div>
            </div>
                  
                  <!-- Pagination indicators -->
                  <div class="flex gap-2 mt-4">
                    <div 
                      v-for="(service, index) in serviceCategories[1].services" 
                      :key="index" 
                      class="relative block h-1.5 w-6 rounded-xs bg-zinc-200/35 overflow-hidden cursor-pointer transition-all duration-300 hover:bg-mint-200/35" 
                      @click="selectService(1, index)"
                    >
                      <div class="absolute inset-0 bg-zinc-200/35"></div>
                      <div 
                        class="absolute inset-0 origin-left transition-all duration-300" 
                        :class="index === activeServices[1] ? 'bg-mint-500' : 'bg-zinc-400/60 scale-x-0'"
                        :style="index === activeServices[1] && !userInteracted[1] && autoPlayProgress[1] > 0 ? `transform: scaleX(${autoPlayProgress[1] / 100})` : index === activeServices[1] ? 'transform: scaleX(0)' : ''"
                      ></div>
                    </div>
                  </div>
                </div>

            <!-- Text Content -->
            <div class="space-y-8 lg:order-2 max-w-lg">
              <div class="space-y-4 text-left">
                <h3 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white dark:text-zinc-900">
                  Krachtig in gebruik.
                </h3>
                  <p class="text-lg md:text-xl text-zinc-300 dark:text-zinc-600 leading-relaxed">
                    We maken slimme oplossingen die processen versnellen, systemen verbinden en data overzichtelijk maken. Denk aan maatwerksoftware die naadloos aansluit op jouw werkwijze.
                  </p>
              </div>
            </div>
          </div>

          <!-- Section 3: Image Top, Text Center -->
          <div ref="section3Ref">
            <!-- Image Content -->
            <div class="relative max-w-4xl mx-auto h-[160vh] -mb-16 sm:-mb-20 md:-mb-28">
              <div class="absolute left-1/2 -translate-x-1/2 h-[160vh] w-screen rounded-lg overflow-hidden shadow-lg dark:shadow-black/30 border border-zinc-700 dark:border-zinc-200">
                <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-black via-black/95 to-black/80"></div>
                
                <!-- Static image instead of cycling through services -->
                <img src="https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&auto=format&fit=crop&q=80" alt="Slim ontwikkeld. Krachtig in gebruik. Flexibel in alles." class="absolute inset-0 w-full h-full object-cover object-center" />
                <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent"></div>
              </div>
                <!-- Text Content with expanded trigger area -->
                <div ref="serviceStartTextAnimation" class="absolute left-0 bottom-0 text-left max-w-lg space-y-8 pb-16 sm:pb-20 md:pb-28 pt-48">
                <div class="space-y-4">
                    <h3 ref="serviceTitleRef" class="text-3xl md:text-4xl lg:text-5xl font-bold text-white dark:text-zinc-900">
                    Flexibel in alles.
                    </h3>
                    <p ref="serviceSubtitleRef" class="text-lg md:text-xl text-zinc-300 dark:text-zinc-600 leading-relaxed">
                        Onze expertise ligt in het ontwikkelen van gebruiksvriendelijke interfaces en robuuste backends die samen een naadloze gebruikerservaring creëren.
                    </p>
                    <p ref="serviceSubtitle2Ref" class="text-lg md:text-xl text-zinc-300 dark:text-zinc-600 leading-relaxed">
                        We combineren moderne technologieën met bewezen ontwikkelmethoden om oplossingen te bouwen die meegroeien met jouw ambities. Van conceptuele ontwerpen tot productierijpe applicaties - elk project krijgt de aandacht en vakmanschap die het verdient.
                    </p>
                </div>
                
                <!-- CTA Button -->
                <Button ref="serviceButtonRef" :href="'/services'" variant="gradient" icon="arrow-right" iconPosition="right" class="transform transition-all duration-200 hover:scale-105">
                    Onze services
                </Button>
                </div>
            </div>
            </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Rounded edges for pagination indicators */
.rounded-xs {
  border-radius: 0.125rem;
}

/* Smooth crossfade animation for images */
.smooth-fade-enter-active {
  transition: all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.smooth-fade-leave-active {
  transition: all 200ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.smooth-fade-enter-from {
  opacity: 0;
  transform: scale(1.02);
}

.smooth-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* Smooth text transitions with slide effect */
.text-slide-enter-active {
  transition: all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 100ms;
}

.text-slide-leave-active {
  transition: all 200ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.text-slide-enter-from {
  opacity: 0;
  transform: translateY(15px);
}

.text-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Ensure buttons maintain their state during transitions */
button:disabled {
  pointer-events: none;
  transition: all 300ms ease;
}

/* Progress fill animation */
@keyframes progress-fill {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.animate-progress-fill {
  animation: progress-fill linear;
  transform-origin: left;
}

/* Pause animation on hover */
.animate-progress-fill:hover {
  animation-play-state: paused;
}

/* Smooth button text transitions */
.button-text-container {
  overflow: hidden;
}

/* Smooth button width transitions */
.button-width-transition {
  transition: width 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94), min-width 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  min-width: fit-content;
}

.button-text-enter-active {
  transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 50ms;
}

.button-text-leave-active {
  transition: all 150ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.button-text-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.button-text-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Fade-in animation for section */
#services-showcase {
  transition: opacity 1s ease, transform 1s ease;
}

#services-showcase.opacity-0 {
  opacity: 0;
  transform: translateY(20px);
}

#services-showcase.opacity-100 {
  opacity: 1;
  transform: translateY(0);
}
</style>
